import { supabase } from '@/integrations/supabase/client';

export interface Highlight {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  author_name: string;
  title: string;
  description: string;
  category: 'achievement' | 'game' | 'team' | 'player';
  location?: string;
  featured: boolean;
  points_cost: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface SubmitHighlightData {
  title: string;
  description: string;
  category: 'achievement' | 'game' | 'team' | 'player';
  location?: string;
}

export const highlightsService = {
  // Get all approved highlights
  async getApprovedHighlights(): Promise<Highlight[]> {
    const { data, error } = await supabase
      .from('community_highlights')
      .select('*')
      .eq('status', 'approved')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching approved highlights:', error);
      throw error;
    }

    return (data || []) as unknown as Highlight[];
  },

  // Get featured highlights
  async getFeaturedHighlights(): Promise<Highlight[]> {
    const { data, error } = await supabase
      .from('community_highlights')
      .select('*')
      .eq('status', 'approved')
      .eq('featured', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching featured highlights:', error);
      throw error;
    }

    return (data || []) as unknown as Highlight[];
  },

  // Get user's own submissions
  async getUserSubmissions(userId: string, userType: 'supabase' | 'internal'): Promise<Highlight[]> {
    const { data, error } = await supabase
      .from('community_highlights')
      .select('*')
      .eq('user_id', userId)
      .eq('user_type', userType)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user submissions:', error);
      throw error;
    }

    return (data || []) as unknown as Highlight[];
  },

  // Submit a new highlight
  async submitHighlight(
    data: SubmitHighlightData,
    userId: string,
    userType: 'supabase' | 'internal',
    authorName: string
  ): Promise<{ success: boolean; error?: string; highlight?: Highlight }> {
    try {
      // Check user has sufficient points
      const { data: userPoints, error: pointsError } = await supabase
        .from('user_points')
        .select('total_points')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .single();

      if (pointsError) {
        // If no points record, create one with 0 points
        if (pointsError.code === 'PGRST116') {
          return { success: false, error: 'You need at least 100 points to submit a highlight' };
        }
        throw pointsError;
      }

      if (!userPoints || userPoints.total_points < 100) {
        return { success: false, error: 'Insufficient points. You need at least 100 points to submit a highlight.' };
      }

      // Insert the highlight
      const { data: highlight, error: insertError } = await supabase
        .from('community_highlights')
        .insert({
          user_id: userId,
          user_type: userType,
          author_name: authorName,
          title: data.title,
          description: data.description,
          category: data.category,
          location: data.location || null,
          points_cost: 100,
          status: 'pending'
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting highlight:', insertError);
        throw insertError;
      }

      // Deduct points
      const { error: deductError } = await supabase
        .from('user_points')
        .update({ total_points: userPoints.total_points - 100 })
        .eq('user_id', userId)
        .eq('user_type', userType);

      if (deductError) {
        console.error('Error deducting points:', deductError);
        // Note: highlight was still created, but points weren't deducted
      }

      // Record transaction
      await supabase
        .from('points_transactions')
        .insert({
          user_id: userId,
          user_type: userType,
          points: -100,
          transaction_type: 'highlight_submission',
          description: `Submitted highlight: ${data.title}`
        });

      return { success: true, highlight: highlight as unknown as Highlight };
    } catch (error) {
      console.error('Error submitting highlight:', error);
      return { success: false, error: 'Failed to submit highlight. Please try again.' };
    }
  },

  // Admin: Approve a highlight
  async approveHighlight(highlightId: string): Promise<boolean> {
    const { error } = await supabase
      .from('community_highlights')
      .update({ status: 'approved', updated_at: new Date().toISOString() })
      .eq('id', highlightId);

    if (error) {
      console.error('Error approving highlight:', error);
      return false;
    }

    return true;
  },

  // Admin: Reject a highlight
  async rejectHighlight(highlightId: string): Promise<boolean> {
    const { error } = await supabase
      .from('community_highlights')
      .update({ status: 'rejected', updated_at: new Date().toISOString() })
      .eq('id', highlightId);

    if (error) {
      console.error('Error rejecting highlight:', error);
      return false;
    }

    return true;
  },

  // Admin: Toggle featured status
  async toggleFeatured(highlightId: string, featured: boolean): Promise<boolean> {
    const { error } = await supabase
      .from('community_highlights')
      .update({ featured, updated_at: new Date().toISOString() })
      .eq('id', highlightId);

    if (error) {
      console.error('Error toggling featured:', error);
      return false;
    }

    return true;
  },

  // Admin: Get all highlights (including pending)
  async getAllHighlights(): Promise<Highlight[]> {
    const { data, error } = await supabase
      .from('community_highlights')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching all highlights:', error);
      throw error;
    }

    return (data || []) as unknown as Highlight[];
  },

  // Admin: Get pending highlights
  async getPendingHighlights(): Promise<Highlight[]> {
    const { data, error } = await supabase
      .from('community_highlights')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching pending highlights:', error);
      throw error;
    }

    return (data || []) as unknown as Highlight[];
  }
};
